import {
  describeDatasetSettings,
  MARKET_PRICE_SETTING_OPTIONS,
} from "constants/marketPriceSetting";
import { sizer } from "layout/styles/styled/sizer";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { FileUpload } from "primereact/fileupload";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { MultiSelect } from "primereact/multiselect";
import { ProgressBar } from "primereact/progressbar";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetMarketPriceDatasetById,
  useUpdateMarketPriceDataset,
  useGetMarketPriceDatasetDownloadUrl,
} from "store/queries/marketPrices";
import { useGetAllProvidersQuery } from "store/queries/providers";
import styled from "styled-components";
import { MarketPriceSetting } from "types/marketPrice";

const PageContainer = styled.div`
  padding: ${sizer(4)};
  max-width: 800px;
  margin: 0 auto;
`;

const FormField = styled.div`
  margin-bottom: ${sizer(3)};
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  margin-bottom: ${sizer(1)};
  color: #495057;
`;

const RequiredStar = styled.span`
  color: #e74c3c;
  margin-left: ${sizer(0.5)};
`;

const HelperText = styled.small`
  color: #6c757d;
  display: block;
  margin-top: ${sizer(1)};
`;

const FileInfo = styled.div`
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: ${sizer(2)};
  margin-bottom: ${sizer(3)};
`;

const CurrentFileInfo = styled.div`
  background-color: #e3f2fd;
  border: 1px solid #2196f3;
  border-radius: 4px;
  padding: ${sizer(2)};
  margin-bottom: ${sizer(2)};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${sizer(2)};
  justify-content: flex-end;
  margin-top: ${sizer(4)};
`;

export function MarketPricesEdit() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const fileUploadRef = useRef<FileUpload>(null);
  const toast = useRef<Toast>(null);

  const { data: dataset, isLoading: isLoadingDataset } =
    useGetMarketPriceDatasetById(id || "");
  const [updateMarketPrices] = useUpdateMarketPriceDataset();
  const [getDownloadUrl] = useGetMarketPriceDatasetDownloadUrl();

  const [displayName, setDisplayName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedProviderIds, setSelectedProviderIds] = useState<string[]>([]);
  const [defaultSetting, setDefaultSetting] =
    useState<MarketPriceSetting | null>(null);
  const [isComparisonSetting, setIsComparisonSetting] = useState(false);

  const { data: providers } = useGetAllProvidersQuery();

  // Initialize form with existing data
  useEffect(() => {
    if (dataset) {
      setDisplayName(dataset.displayName);
      setDescription(dataset.description || "");
      setSelectedProviderIds(dataset.providerIds || []);
      // Datasets uploaded before CLE-2939 have no defaultSetting; leave it unchosen.
      setDefaultSetting(dataset.metadata?.defaultSetting ?? null);
    }
  }, [dataset]);

  // CLE-2939: the API needs a setting to tag a replacement file's rows with, and to
  // record as the providers' own setting. Without either, the setting is not sent.
  const isSettingRequired = Boolean(selectedFile) || isComparisonSetting;

  // Get provider options for multiselect
  const providerOptions =
    providers?.data?.map((provider: any) => ({
      label: provider.name,
      value: provider.id,
    })) || [];

  const handleFileSelect = (event: any) => {
    const file = event.files[0];
    if (file && file.name.endsWith(".csv")) {
      setSelectedFile(file);
      fileUploadRef.current?.clear();
    } else {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Please select a valid CSV file",
      });
      fileUploadRef.current?.clear();
    }
  };

  const handleDownloadCurrent = async () => {
    if (!dataset) return;

    try {
      const response = await getDownloadUrl(dataset.id).unwrap();
      if (response.url) {
        window.open(response.url, "_blank");
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.data?.message || "Failed to get download URL",
      });
    }
  };

  const handleSubmit = async () => {
    if (!displayName.trim()) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Display name is required",
      });
      return;
    }

    if (!selectedProviderIds.length) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Please select at least one provider",
      });
      return;
    }

    if (isSettingRequired && !defaultSetting) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Please select a setting",
      });
      return;
    }

    setIsUploading(true);
    try {
      await updateMarketPrices({
        id: id!,
        file: selectedFile || undefined,
        displayName: displayName.trim(),
        description: description.trim() || undefined,
        providerIds: selectedProviderIds,
        ...(isSettingRequired && defaultSetting
          ? { defaultSetting, isComparisonSetting }
          : {}),
      }).unwrap();

      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Market prices updated successfully",
      });
      navigate(`/payer-rules?tab=4`); // Navigate to Market Prices tab
    } catch (error: any) {
      console.error("Update error:", error);
      // Extract error message from various possible error structures
      let errorMessage = "Failed to update market prices";

      if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (error?.error?.data?.message) {
        errorMessage = error.error.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      // For longer error messages, show them in a more readable format
      if (errorMessage.length > 100) {
        toast.current?.show({
          severity: "error",
          summary: "Update Failed",
          detail: errorMessage,
          life: 10000, // Show for 10 seconds for longer messages
          sticky: errorMessage.length > 200, // Make it sticky for very long messages
        });
      } else {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: errorMessage,
        });
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    navigate(`/payer-rules?tab=4`);
  };

  if (isLoadingDataset) {
    return (
      <PageContainer>
        <div className="flex justify-content-center">
          <ProgressSpinner />
        </div>
      </PageContainer>
    );
  }

  if (!dataset) {
    return (
      <PageContainer>
        <Card>
          <p>Dataset not found</p>
          <Button
            label="Back to Market Prices"
            onClick={() => navigate(`/payer-rules?tab=4`)}
          />
        </Card>
      </PageContainer>
    );
  }

  const datasetSettings = describeDatasetSettings(dataset.metadata);

  return (
    <PageContainer>
      <Toast ref={toast} />
      <Card title="Edit Market Price Dataset">
        <FormField className="mb-4">
          <Label>
            Display Name <RequiredStar>*</RequiredStar>
          </Label>
          <InputText
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g., Q4 2024 Market Rates"
            className="w-full"
            disabled={isUploading}
          />
        </FormField>

        <FormField className="mb-4">
          <Label>Description</Label>
          <InputTextarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description of this market price dataset"
            rows={3}
            className="w-full"
            disabled={isUploading}
          />
        </FormField>

        <FormField className="mb-4">
          <Label>
            Provider(s) <RequiredStar>*</RequiredStar>
          </Label>
          <MultiSelect
            value={selectedProviderIds}
            options={providerOptions}
            onChange={(e) => setSelectedProviderIds(e.value)}
            placeholder="Select Provider(s)"
            className="w-full"
            filter
            display="chip"
            showClear
            disabled={isUploading}
          />
          <HelperText>
            Select the providers this market price data applies to. The same
            rates will be applied to all selected providers.
          </HelperText>
        </FormField>

        <FormField className="mb-4">
          <Label>
            Setting {isSettingRequired && <RequiredStar>*</RequiredStar>}
          </Label>
          <Dropdown
            value={defaultSetting}
            options={MARKET_PRICE_SETTING_OPTIONS}
            onChange={(e) => setDefaultSetting(e.value ?? null)}
            placeholder="Select a setting"
            className="w-full"
            disabled={isUploading}
          />
          <HelperText>
            Used for every row of a replacement CSV whose optional setting
            column is blank. Prices already uploaded keep their setting.
            Required when you choose a new file.
          </HelperText>
          <div className="flex align-items-center gap-2 mt-3">
            <Checkbox
              inputId="market-prices-edit-comparison-setting"
              checked={isComparisonSetting}
              onChange={(e) => setIsComparisonSetting(e.checked ?? false)}
              disabled={isUploading}
            />
            <label htmlFor="market-prices-edit-comparison-setting">
              This is the provider&apos;s own setting (used for comparison)
            </label>
          </div>
          <HelperText>
            Sets the default tab in Smart Insights and what the comparison and
            AI insights use.
          </HelperText>
        </FormField>

        <FormField>
          <Label>CSV File</Label>
          <CurrentFileInfo>
            <div>
              <strong>Current file:</strong> {dataset.fileName}
            </div>
            <div>
              <strong>Uploaded:</strong>{" "}
              {new Date(dataset.createdAt).toLocaleDateString()}
            </div>
            <div>
              <strong>Total prices:</strong>{" "}
              {/* eslint-disable-next-line no-underscore-dangle */}
              {dataset._count?.marketPrices || 0}
            </div>
            {datasetSettings.length > 0 && (
              <div>
                <strong>Setting(s):</strong> {datasetSettings.join(", ")}
              </div>
            )}
            <Button
              label="Download Current File"
              icon="pi pi-download"
              className="p-button-text p-button-sm mt-2"
              onClick={handleDownloadCurrent}
              disabled={isUploading}
            />
          </CurrentFileInfo>

          {selectedFile ? (
            <FileInfo>
              <div>
                <strong>New file selected:</strong> {selectedFile.name}
              </div>
              <div>
                <strong>Size:</strong> {(selectedFile.size / 1024).toFixed(2)}{" "}
                KB
              </div>
              <Button
                label="Remove"
                icon="pi pi-times"
                className="p-button-text p-button-danger p-button-sm mt-2"
                onClick={() => setSelectedFile(null)}
                disabled={isUploading}
              />
            </FileInfo>
          ) : (
            <>
              <FileUpload
                ref={fileUploadRef}
                mode="basic"
                name="file"
                accept=".csv"
                maxFileSize={10000000} // 10MB
                chooseLabel="Choose New CSV File (Optional)"
                className="w-full"
                onSelect={handleFileSelect}
                disabled={isUploading}
              />
              <HelperText className="mt-2">
                Upload a new CSV file to replace the existing market prices.
                This will update existing prices and add new ones, but
                won&apos;t delete any prices not in the new file.
              </HelperText>
            </>
          )}
          <HelperText className="mt-2">
            Maximum file size: 10MB. The CSV must include columns for
            entity_name, Payer Name, CPT Code, and rate.
          </HelperText>
        </FormField>

        {isUploading && (
          <ProgressBar mode="indeterminate" style={{ height: "6px" }} />
        )}

        <ButtonGroup>
          <Button
            label="Cancel"
            icon="pi pi-times"
            className="p-button-text"
            onClick={handleCancel}
            disabled={isUploading}
          />
          <Button
            label={isUploading ? "Updating..." : "Update"}
            icon={isUploading ? "pi pi-spin pi-spinner" : "pi pi-save"}
            onClick={handleSubmit}
            disabled={
              isUploading ||
              !displayName.trim() ||
              (isSettingRequired && !defaultSetting)
            }
          />
        </ButtonGroup>
      </Card>
    </PageContainer>
  );
}

export default MarketPricesEdit;
